import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, API } from "@/lib/api";
import { ArrowRight } from "lucide-react";

export function heroUrl(assetId) {
  return assetId ? `${API}/forms/example/${assetId}` : null;
}

// Centre-screen broadcast shown to agency users — platform logo, hero image, rich message + optional CTA.
export function TransmissionModal({ t, logoUrl, onClose, onAck }) {
  if (!t) return null;
  const hero = heroUrl(t.hero_asset_id);
  const cta = t.cta;
  const acknowledge = () => { onAck && onAck(t); onClose && onClose(); };
  const clickCta = () => {
    if (!cta?.link) { acknowledge(); return; }
    if (onAck) onAck(t);
    if (cta.link_type === "internal") window.location.assign(cta.link);
    else window.open(cta.link, "_blank", "noopener");
    onClose && onClose();
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) acknowledge(); }}>
      <DialogContent className="bg-white border-[#EEEFEF] text-[#06060C] max-w-lg p-0 overflow-hidden rounded-3xl" data-testid="transmission-modal">
        <div className="flex flex-col items-center px-8 pt-7 pb-3">
          {logoUrl ? <img src={logoUrl} alt="" className="h-9 w-9 rounded-lg object-cover border border-[#EEEFEF]" /> : <img src="/shuttle-logo.png" alt="" className="h-9 w-9 object-contain" />}
        </div>
        {hero && (
          <div className="px-6">
            <div className="w-full rounded-2xl overflow-hidden border border-[#f0f0f3] bg-[#F6F6FD]" style={{ aspectRatio: "16 / 9" }}>
              <img src={hero} alt="" className="w-full h-full object-cover" data-testid="transmission-hero" />
            </div>
          </div>
        )}
        <DialogHeader className="px-8 pt-5">
          <DialogTitle className="font-display text-2xl font-semibold text-center" data-testid="transmission-title">{t.title || "Transmission"}</DialogTitle>
          <DialogDescription className="sr-only">Broadcast message</DialogDescription>
        </DialogHeader>
        <div className="px-8 pb-2">
          <div className="ql-snow"><div className="ql-editor !p-0 text-[15px] leading-relaxed text-[#3a3a42] transmission-body" data-testid="transmission-body" dangerouslySetInnerHTML={{ __html: t.message_html || "" }} /></div>
        </div>
        <div className="px-8 pb-8 pt-3 flex flex-col items-center gap-2">
          {cta?.text ? (
            <Button data-testid="transmission-cta" onClick={clickCta} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full px-6 h-11 font-semibold">{cta.text} <ArrowRight className="h-4 w-4 ml-1.5" /></Button>
          ) : (
            <Button data-testid="transmission-dismiss" onClick={acknowledge} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full px-6 h-11 font-semibold">Got it</Button>
          )}
          {cta?.text && <button data-testid="transmission-dismiss" onClick={acknowledge} className="text-[11px] text-[#9FA1A4] hover:text-[#5b2fd6]">Dismiss</button>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Auto-popup queue: shows unseen transmissions one at a time to a recipient.
export function TransmissionQueue({ logoUrl }) {
  const [queue, setQueue] = useState([]);
  useEffect(() => { api.get("/console/transmissions/pending").then(({ data }) => setQueue(data.transmissions || [])).catch(() => {}); }, []);
  if (!queue.length) return null;
  const ack = async (tr) => { try { await api.post(`/console/transmissions/${tr.id}/ack`); } catch (e) { /* ignore */ } };
  return <TransmissionModal t={queue[0]} logoUrl={logoUrl} onAck={ack} onClose={() => setQueue((q) => q.slice(1))} />;
}

// Fetch-and-show a single transmission by id (used when opening from the bell/inbox).
export function TransmissionById({ id, logoUrl, onClose }) {
  const [t, setT] = useState(null);
  useEffect(() => { if (!id) { setT(null); return; } api.get(`/console/transmissions/${id}`).then(({ data }) => setT(data)).catch(() => onClose && onClose()); }, [id]);
  if (!t) return null;
  const ack = async (tr) => { try { await api.post(`/console/transmissions/${tr.id}/ack`); } catch (e) { /* ignore */ } };
  return <TransmissionModal t={t} logoUrl={logoUrl} onAck={ack} onClose={onClose} />;
}
